import useDraft from "../hooks/useDraft";
import DraftBanner from "../components/DraftBanner";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import { RECEIPT_ACCEPT, RECEIPT_MAX_BYTES, apiError, evaluateFundings, fundingPayload, nextPieceId, receiptProblem, toMinor } from "../lib/finance";
import FundingRows from "../components/finance/FundingRows";
import SupplierCombobox from "../components/finance/SupplierCombobox";
import ReceiptPreview, { ReceiptPreviewDialog, receiptSource } from "../components/finance/ReceiptPreview";
import { piecePath } from "../components/finance/ReceiptInbox";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Skeleton } from "../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../components/ui/collapsible";
import { toast } from "sonner";
import { ArrowLeft, ChevronDown, FileText, Lightbulb, Loader2, Plus, RotateCcw, SkipForward, UploadCloud, Wallet, X } from "lucide-react";

const ACCEPT = RECEIPT_ACCEPT;
const MAX_BYTES = RECEIPT_MAX_BYTES;

const emptyForm = (currency) => ({
  title: "", amount: "", currency, category_id: "", supplier: "", supplier_id: "", fundings: [],
  incurred_at: "", invoice_reference: "", project: "", beneficiary: "", description: "", preconstitution: false,
});

function Section({ title, hint, children, testid }) {
  return (
    <Card className="p-4 sm:p-5 space-y-4" data-testid={testid}>
      <div>
        <h3 className="font-display font-semibold text-base text-slate-900">{title}</h3>
        {hint && <p className="text-xs text-slate-500 mt-0.5">{hint}</p>}
      </div>
      {children}
    </Card>
  );
}

function Help({ children }) {
  return <p className="text-xs text-slate-500">{children}</p>;
}

export default function FinanceExpenseFormPage() {
  const { id } = useParams();
  const editing = Boolean(id);
  // Serial entry: `?piece=` is a receipt waiting in « À saisir »; saving moves on to the next one.
  const [searchParams] = useSearchParams();
  const pieceId = editing ? null : searchParams.get("piece");
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManageCategories = hasPermission(user, "finance:manage_categories");
  const canViewAll = hasPermission(user, "finance:view_all");
  // One key per opening of the form: a double click never creates two drafts.
  const idempotencyKey = useRef(crypto.randomUUID());
  const [pieces, setPieces] = useState([]); // the caller's waiting receipts, in serial-entry mode
  const seriesCurrency = useRef(null); // a series keeps the currency of the previous receipt
  const fileInput = useRef(null);

  const [status, setStatus] = useState("loading"); // loading | ready | error | locked
  const [loadError, setLoadError] = useState("");
  const [form, setForm] = useState(emptyForm("EUR"));
  const [version, setVersion] = useState(null);
  // An imported, subscription or purchase draft may wait for its receipt; "import" | "subscription" | "purchase" | false.
  const [imported, setImported] = useState(false);
  const [categories, setCategories] = useState([]);
  const [currencies, setCurrencies] = useState(["EUR"]);
  const [preview, setPreview] = useState(null); // {source, name} of the receipt open in the preview dialog
  const [files, setFiles] = useState([]); // {key, id?, name, state: uploading|ready|error|existing, file?}
  const [showErrors, setShowErrors] = useState(false);
  const [saving, setSaving] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [categoryDraft, setCategoryDraft] = useState(null);

  const load = useCallback(async () => {
    setStatus("loading");
    try {
      const [settings, cats, expense, inbox] = await Promise.all([
        api.get("/finance/settings"), api.get("/finance/categories"),
        editing ? api.get(`/finance/expenses/${id}`) : Promise.resolve(null),
        pieceId ? api.get("/finance/inbox") : Promise.resolve(null),
      ]);
      setCurrencies(settings.data.currencies);
      setCategories(cats.data);
      if (!expense) {
        // A fresh form (and a fresh idempotency key) for every piece of a series.
        idempotencyKey.current = crypto.randomUUID();
        setForm(emptyForm((pieceId && seriesCurrency.current) || settings.data.default_currency));
        setShowErrors(false);
        setMoreOpen(false);
        setCategoryDraft(null);
        if (pieceId) {
          const waiting = inbox.data.items;
          const piece = waiting.find((p) => p.id === pieceId);
          setPieces(waiting);
          if (!piece) { setStatus("piece_gone"); return; }
          setFiles([{ key: piece.id, id: piece.id, name: piece.name, state: "piece" }]);
        } else {
          setFiles([]);
        }
        setStatus("ready");
        return;
      }
      const e = expense.data;
      if (e.status !== "draft") { setStatus("locked"); return; }
      setVersion(e.version);
      setImported(["import", "subscription", "purchase"].includes(e.source_system) && e.source_system);
      setForm({
        title: e.title, amount: e.amount, currency: e.currency, category_id: e.category_id, supplier: e.supplier || "", supplier_id: e.supplier_id || "",
        incurred_at: e.incurred_at || "", invoice_reference: e.invoice_reference || "", project: e.project || "",
        beneficiary: e.beneficiary || "", description: e.description || "", preconstitution: !!e.preconstitution,
        fundings: e.fundings.map((f) => ({
          payer_type: f.payer_type, collaborator_id: f.collaborator_id, collaborator_label: f.payer_type === "collaborator" ? f.payer_label : "",
          third_party_name: f.payer_type === "third_party" ? f.payer_label : "", amount: f.amount, paid_at: f.paid_at || "", note: f.note || "",
        })),
      });
      setFiles((e.attachments || []).map((a) => ({ key: a.id, id: a.id, name: a.name || "Justificatif", state: a.unavailable ? "error" : "existing" })));
      setMoreOpen(Boolean(e.incurred_at || e.invoice_reference || e.project || e.beneficiary || e.description));
      setStatus("ready");
    } catch (err) {
      setLoadError(apiError(err));
      setStatus("error");
    }
  }, [editing, id, pieceId]);

  useEffect(() => { load(); }, [load]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));  // A blank form offers the draft kept on this device (never the receipts: those are uploaded files); a serial-entry or edited one does not.
  const local = useDraft({ name: "expense-new", userId: user?.id, values: form, enabled: !editing && !pieceId && status === "ready", apply: (saved) => setForm((f) => ({ ...f, ...saved })) });
  const evaluation = useMemo(() => evaluateFundings(form.fundings, form.amount, form.currency), [form.fundings, form.amount, form.currency]);
  const readyFiles = files.filter((f) => (f.state === "ready" || f.state === "existing" || f.state === "piece") && f.id);
  const next = pieceId ? nextPieceId(pieces, pieceId) : null;
  const remaining = pieces.filter((p) => p.id !== pieceId).length;
  const uploading = files.some((f) => f.state === "uploading");

  const upload = async (file, key = crypto.randomUUID()) => {
    const problem = receiptProblem(file);
    if (problem) {
      setFiles((old) => [...old.filter((f) => f.key !== key), { key, name: file.name, state: "error", file, reason: problem }]);
      return;
    }
    setFiles((old) => [...old.filter((f) => f.key !== key), { key, name: file.name, state: "uploading", file }]);
    try {
      const data = new FormData();
      data.append("file", file);
      const res = await api.post("/finance/attachments", data, { headers: { "Idempotency-Key": key } });
      setFiles((old) => old.map((f) => (f.key === key ? { ...f, id: res.data.id, state: "ready" } : f)));
    } catch (err) {
      setFiles((old) => old.map((f) => (f.key === key ? { ...f, state: "error", reason: apiError(err) } : f)));
    }
  };
  const addFiles = (list) => Array.from(list || []).forEach((file) => upload(file));
  // The last valid receipt cannot be removed: an expense never exists without one.
  const removable = (item) => item.state === "error" || item.state === "uploading" || readyFiles.length > 1;
  const removeFile = (item) => setFiles((old) => old.filter((f) => f.key !== item.key));
  // A file chosen here is shown from this browser; one already on the server is fetched (owner/scope checked there).
  const previewSource = (item) => {
    if (item.file && item.state !== "error") return receiptSource.local(item.file);
    if (item.state === "piece") return receiptSource.piece(item.id);
    if (item.state === "existing") return receiptSource.attachment(item.id);
    return null;
  };

  const createCategory = async () => {
    try {
      const { data } = await api.post("/finance/categories", { name: categoryDraft.trim() });
      setCategories((old) => [...old, data].sort((a, b) => a.name.localeCompare(b.name, "fr")));
      set("category_id", data.id);
      setCategoryDraft(null);
      toast.success(`Catégorie « ${data.name} » créée`);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const blockers = [];
  if (!form.title.trim()) blockers.push("un libellé");
  if (toMinor(form.amount, form.currency) === null) blockers.push("un montant valide");
  if (!form.category_id) blockers.push("une catégorie");
  if (!readyFiles.length && !imported) blockers.push("au moins un justificatif envoyé");
  if (!evaluation.valid) blockers.push("une répartition « Qui a payé ? » complète");
  const canSave = !saving && !uploading && blockers.length === 0;

  const save = async (e) => {
    e.preventDefault();
    setShowErrors(true);
    if (!canSave) return;
    setSaving(true);
    const body = {
      title: form.title.trim(), amount: form.amount.replace(/[\s  ]/g, "").replace(",", "."), currency: form.currency,
      category_id: form.category_id, supplier: form.supplier || null, supplier_id: form.supplier_id || null, description: form.description.trim(),
      incurred_at: form.incurred_at || null, invoice_reference: form.invoice_reference.trim() || null, project: form.project.trim() || null,
      beneficiary: form.beneficiary.trim() || null, preconstitution: form.preconstitution,
      fundings: form.fundings.map((f) => ({ ...fundingPayload(f), amount: f.amount.replace(/[\s  ]/g, "").replace(",", ".") })),
      attachment_ids: readyFiles.map((f) => f.id),
    };
    try {
      const res = editing
        ? await api.put(`/finance/expenses/${id}`, { ...body, expected_version: version })
        : await api.post("/finance/expenses", body, { headers: { "Idempotency-Key": idempotencyKey.current } });
      if (pieceId) {
        seriesCurrency.current = form.currency;
        toast.success(next ? `Brouillon enregistré. Justificatif suivant (${remaining} restant${remaining > 1 ? "s" : ""})` : "Brouillon enregistré. Tous les justificatifs sont saisis.");
        navigate(next ? piecePath(next) : "/finance?tab=inbox", { replace: true });
        return;
      }
      local.clear();
      toast.success(editing ? "Modifications enregistrées" : "Brouillon enregistré");
      // Only finance:view_all can open the expense detail; others go back to their own view.
      if (canViewAll) navigate("/finance?tab=expenses", { state: { openExpense: res.data.id } });
      else navigate("/finance");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const backTo = pieceId ? "/finance?tab=inbox" : canViewAll ? "/finance?tab=expenses" : "/finance";
  const back = (
    <Link to={backTo} className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
      <ArrowLeft size={14} /> {pieceId ? "Retour aux justificatifs à saisir" : "Retour aux dépenses"}
    </Link>
  );

  if (status === "loading") {
    return <div className="space-y-4">{back}<Skeleton className="h-8 w-64" /><Skeleton className="h-64 w-full max-w-3xl" /><Skeleton className="h-40 w-full max-w-3xl" /></div>;
  }
  if (status === "piece_gone") {
    return (
      <div className="space-y-4">{back}
        <Card className="p-5 max-w-xl space-y-3" data-testid="finance-piece-gone">
          <h2 className="font-display font-semibold text-lg text-slate-900">Ce justificatif n'est plus à saisir</h2>
          <p className="text-sm text-slate-600">Il a déjà été saisi ou retiré, peut-être depuis un autre onglet.</p>
          <div className="flex flex-wrap gap-2">
            {pieces.length > 0 && <Button onClick={() => navigate(piecePath(pieces[0].id), { replace: true })}>Saisir le suivant</Button>}
            <Button variant="outline" onClick={() => navigate("/finance?tab=inbox")}>Voir les justificatifs à saisir</Button>
          </div>
        </Card>
      </div>
    );
  }
  if (status === "error" || status === "locked") {
    return (
      <div className="space-y-4">{back}
        <Card className="p-5 max-w-xl space-y-3">
          <h2 className="font-display font-semibold text-lg text-slate-900">{status === "locked" ? "Cette dépense est soumise" : "Impossible d'ouvrir le formulaire"}</h2>
          <p className="text-sm text-slate-600">{status === "locked" ? "Une dépense soumise ne peut plus être modifiée." : loadError}</p>
          {status === "error" && <Button variant="outline" onClick={load}>Réessayer</Button>}
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="finance-expense-form-page">
      {back}
      <div>
        <h1 className="font-display font-bold text-2xl text-slate-900">{editing ? "Modifier la dépense" : pieceId ? "Saisir le justificatif" : "Nouvelle dépense"}</h1>
        <p className="text-sm text-slate-500">
          {pieceId
            ? `Lisez le justificatif et complétez la dépense. ${remaining ? `Encore ${remaining} justificatif${remaining > 1 ? "s" : ""} après celui-ci.` : "C'est le dernier justificatif à saisir."}`
            : "Enregistrez la dépense, qui l'a payée et sa preuve. Elle reste un brouillon modifiable jusqu'à sa soumission."}
        </p>
      </div>

      <DraftBanner pending={local.pending} onRestore={local.restore} onDismiss={local.dismiss} />

      <form onSubmit={save} className={`grid grid-cols-1 gap-4 items-start ${pieceId ? "lg:grid-cols-[minmax(0,1fr)_420px]" : "lg:grid-cols-[minmax(0,1fr)_300px]"}`} aria-label="Saisir une dépense" noValidate>
        <div className="space-y-4 min-w-0">
          <Section title="La dépense" testid="finance-section-expense">
            <div className="space-y-2">
              <Label htmlFor="fin-title">Libellé *</Label>
              <Input id="fin-title" value={form.title} onChange={(e) => set("title", e.target.value)} maxLength={180} className="text-base md:text-sm"
                placeholder="Ex. : Abonnement internet du bureau, octobre" data-testid="finance-title" aria-invalid={showErrors && !form.title.trim()} />
            </div>
            <div className="grid grid-cols-[minmax(0,1fr)_110px] gap-3">
              <div className="space-y-2">
                <Label htmlFor="fin-amount">Montant total *</Label>
                <Input id="fin-amount" inputMode="decimal" value={form.amount} onChange={(e) => set("amount", e.target.value)} placeholder="0,00"
                  className="text-base md:text-sm tabular-nums" data-testid="finance-amount" aria-invalid={showErrors && toMinor(form.amount, form.currency) === null} />
              </div>
              <div className="space-y-2">
                <Label>Devise</Label>
                <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
                  <SelectTrigger data-testid="finance-currency" aria-label="Devise"><SelectValue /></SelectTrigger>
                  <SelectContent>{currencies.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {showErrors && form.amount && toMinor(form.amount, form.currency) === null && (
              <p className="text-xs text-red-700 -mt-2">Montant invalide{form.currency === "XOF" ? " (le franc CFA n'a pas de centimes)" : ""}.</p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Catégorie *</Label>
                {categoryDraft === null ? (
                  <div className="flex gap-2">
                    <Select value={form.category_id} onValueChange={(v) => set("category_id", v)} disabled={!categories.length}>
                      <SelectTrigger data-testid="finance-category" aria-label="Catégorie"><SelectValue placeholder={categories.length ? "Choisir" : "Aucune catégorie"} /></SelectTrigger>
                      <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                    {canManageCategories && (
                      <Button type="button" variant="outline" size="icon" className="shrink-0" aria-label="Créer une catégorie" onClick={() => setCategoryDraft("")}><Plus size={16} /></Button>
                    )}
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input autoFocus value={categoryDraft} onChange={(e) => setCategoryDraft(e.target.value)} maxLength={100} placeholder="Nom de la catégorie"
                      className="text-base md:text-sm" aria-label="Nom de la nouvelle catégorie" data-testid="finance-new-category"
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (categoryDraft.trim()) createCategory(); } }} />
                    <Button type="button" onClick={createCategory} disabled={!categoryDraft.trim()}>Créer</Button>
                    <Button type="button" variant="ghost" size="icon" aria-label="Annuler" onClick={() => setCategoryDraft(null)}><X size={16} /></Button>
                  </div>
                )}
                {!categories.length && categoryDraft === null && (
                  <Help>{canManageCategories ? "Créez une première catégorie avec le bouton +." : "Aucune catégorie n'existe encore : demandez à un responsable Finance d'en créer une."}</Help>
                )}
              </div>
              <div className="space-y-2">
                <Label>Fournisseur</Label>
                <SupplierCombobox value={form.supplier} supplierId={form.supplier_id} onChange={(v) => setForm((f) => ({ ...f, ...v }))} />
                <Help>Qui a vendu ou facturé. Ex. : Orange Sénégal, Papeterie Centrale.</Help>
              </div>
            </div>
            <label className="flex items-start gap-2 text-sm">
              <Checkbox checked={form.preconstitution} onCheckedChange={(v) => set("preconstitution", v === true)} className="mt-0.5" data-testid="finance-preconstitution" />
              <span>
                <span className="text-slate-900">Dépense antérieure à la création de la société</span>
                <span className="block text-xs text-slate-500">Cochez si elle a été engagée avant l'immatriculation de Jamora : elle restera identifiable pour la décision collective à venir.</span>
              </span>
            </label>
          </Section>

          <Section title="Qui a payé ?" hint="Une ligne par payeur, avec la date à laquelle il a réellement payé." testid="finance-section-payers">
            <FundingRows fundings={form.fundings} onChange={(v) => set("fundings", v)} currency={form.currency} evaluation={evaluation} showErrors={showErrors} />
          </Section>

          <Section title={imported ? "Justificatifs" : "Justificatifs *"}
            hint={imported === "purchase" ? "Brouillon créé depuis une demande d'achat : ajoutez la facture du fournisseur. Elle reste obligatoire pour soumettre la dépense."
              : imported === "subscription" ? "Brouillon créé par un abonnement : ajoutez la facture de cette échéance. Elle reste obligatoire pour soumettre la dépense."
              : imported ? "Dépense importée d'un tableur : ajoutez le justificatif dès que vous l'avez. Il reste obligatoire pour soumettre la dépense." : "Facture, reçu ou capture du paiement. Au moins une pièce est obligatoire, dès le brouillon."}
            testid="finance-section-receipts">
            <div
              className="rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-center"
              onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}>
              <UploadCloud size={22} className="mx-auto text-slate-400" aria-hidden="true" />
              <p className="text-sm text-slate-700 mt-1">Déposez vos fichiers ici, ou</p>
              <Button type="button" variant="outline" size="sm" className="mt-2" onClick={() => fileInput.current?.click()} data-testid="finance-pick-files">
                Choisir des fichiers
              </Button>
              <input ref={fileInput} type="file" multiple accept={ACCEPT} className="sr-only" data-testid="finance-file-input" tabIndex={-1}
                onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
              <p className="text-xs text-slate-500 mt-2">PDF ou photo (JPEG, PNG, WebP), 10 Mo maximum par fichier.</p>
            </div>
            {files.length > 0 && (
              <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200" data-testid="finance-files">
                {files.map((item) => (
                  <li key={item.key} className="flex items-center gap-3 px-3 py-2 text-sm">
                    <FileText size={16} className="text-slate-400 shrink-0" aria-hidden="true" />
                    <span className="min-w-0 flex-1">
                      {previewSource(item) ? (
                        <button type="button" className="block max-w-full truncate text-left text-slate-900 hover:text-jamora hover:underline"
                          onClick={() => setPreview({ source: previewSource(item), name: item.name })} aria-label={`Voir ${item.name}`} data-testid="finance-file-open">
                          {item.name}
                        </button>
                      ) : (
                        <span className="block truncate text-slate-900">{item.name}</span>
                      )}
                      <span className={`block text-xs ${item.state === "error" ? "text-red-700" : "text-slate-500"}`}>
                        {item.state === "uploading" ? "Envoi en cours…" : item.state === "error" ? (item.reason || "Échec de l'envoi") : item.state === "existing" ? "Déjà rattaché" : item.state === "piece" ? "Justificatif déposé, affiché à côté" : "Prêt"}
                      </span>
                    </span>
                    {item.state === "uploading" && <Loader2 size={15} className="animate-spin text-slate-400" />}
                    {item.state === "error" && item.file && item.file.size <= MAX_BYTES && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => upload(item.file, item.key)}><RotateCcw size={14} className="mr-1" /> Réessayer</Button>
                    )}
                    {item.state !== "uploading" && (
                      <Button type="button" variant="ghost" size="sm" disabled={!removable(item)} onClick={() => removeFile(item)}
                        title={removable(item) ? undefined : "Ajoutez une autre pièce avant de retirer celle-ci"} data-testid="finance-remove-file">
                        Retirer
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {readyFiles.length === 1 && files.length > 0 && <Help>La dernière pièce valide ne peut pas être retirée : ajoutez d'abord celle qui la remplace.</Help>}
            {showErrors && !readyFiles.length && !imported && <p role="alert" className="text-sm text-red-700">Ajoutez au moins un justificatif.</p>}
          </Section>

          <Collapsible open={moreOpen} onOpenChange={setMoreOpen}>
            <Card className="p-4 sm:p-5">
              <CollapsibleTrigger asChild>
                <button type="button" className="w-full flex items-center justify-between text-left" data-testid="finance-more-toggle">
                  <span>
                    <span className="block font-display font-semibold text-base text-slate-900">Informations complémentaires</span>
                    <span className="block text-xs text-slate-500 mt-0.5">Facultatif : date de commande, référence, projet, bénéficiaire, description.</span>
                  </span>
                  <ChevronDown size={18} className={`text-slate-400 transition-transform ${moreOpen ? "rotate-180" : ""}`} />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="fin-incurred">Date de la commande ou de la prestation</Label>
                    <Input id="fin-incurred" type="date" value={form.incurred_at} onChange={(e) => set("incurred_at", e.target.value)} className="text-base md:text-sm" data-testid="finance-incurred-at" />
                    <Help>Quand la dépense a été décidée ou réalisée, si c'est différent du paiement. Ex. : matériel commandé le 2 septembre, payé le 5 octobre. Laissez vide si vous ne la connaissez pas.</Help>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="fin-invoice">Référence de la facture</Label>
                    <Input id="fin-invoice" value={form.invoice_reference} onChange={(e) => set("invoice_reference", e.target.value)} maxLength={120} className="text-base md:text-sm" />
                    <Help>Le numéro inscrit sur la facture, pour la retrouver. Ex. : FAC-2026-042.</Help>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="fin-project">Projet</Label>
                    <Input id="fin-project" value={form.project} onChange={(e) => set("project", e.target.value)} maxLength={120} className="text-base md:text-sm" />
                    <Help>L'initiative à laquelle rattacher ce coût, pour savoir ce qu'elle a coûté au total. Ex. : Pilote pharmacie Liberté 6, Salon Pharmagora 2026. Ce n'est ni le fournisseur (qui vend) ni le payeur (qui règle).</Help>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="fin-beneficiary">Bénéficiaire</Label>
                    <Input id="fin-beneficiary" value={form.beneficiary} onChange={(e) => set("beneficiary", e.target.value)} maxLength={180} className="text-base md:text-sm" />
                    <Help>La personne ou l'équipe pour qui la dépense est faite, si ce n'est pas Jamora en général. Ex. : Moussa Diop (téléphone fourni), équipe terrain de Thiès.</Help>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fin-description">Description</Label>
                  <Textarea id="fin-description" value={form.description} onChange={(e) => set("description", e.target.value)} maxLength={2000} rows={3} className="text-base md:text-sm" />
                  <Help>Ce qui a été acheté, ou pourquoi. Ex. : 3 cartouches d'encre pour l'imprimante du bureau.</Help>
                </div>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {showErrors && blockers.length > 0 && (
            <p role="alert" className="text-sm text-red-700" data-testid="finance-blockers">Pour enregistrer, il manque : {blockers.join(", ")}.</p>
          )}
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => navigate(backTo)} className="h-11 sm:h-10">{pieceId ? "Arrêter la saisie" : "Annuler"}</Button>
            {next && (
              <Button type="button" variant="outline" onClick={() => navigate(piecePath(next), { replace: true })} disabled={saving} className="h-11 sm:h-10" data-testid="finance-piece-skip">
                <SkipForward size={14} className="mr-1.5" /> Passer
              </Button>
            )}
            <Button type="submit" disabled={saving || uploading} data-testid="finance-save" className="h-11 sm:h-10">
              {(saving || uploading) && <Loader2 size={14} className="animate-spin mr-1.5" />}
              {uploading ? "Envoi des fichiers…" : editing ? "Enregistrer les modifications" : next ? "Enregistrer et suivant" : "Enregistrer le brouillon"}
            </Button>
          </div>
        </div>

        <aside className={`space-y-4 lg:sticky lg:top-4 ${pieceId ? "order-first lg:order-none" : ""}`}>
          {pieceId && (
            <Card className="p-3">
              <ReceiptPreview source={receiptSource.piece(pieceId)} name={files.find((f) => f.state === "piece")?.name || "justificatif"} />
            </Card>
          )}
          {!pieceId && <>
          <Card className="p-4 space-y-2 bg-slate-50 border-slate-200">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-900"><Lightbulb size={15} className="text-jamora" /> Brouillon, puis soumission</div>
            <p className="text-xs text-slate-600">Le brouillon reste modifiable. Une fois soumise, la dépense est figée. Soumettre ne vaut ni approbation ni remboursement.</p>
          </Card>
          <Card className="p-4 space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-900"><Wallet size={15} className="text-jamora" /> Payée ou non ?</div>
            <p className="text-xs text-slate-600">Sans paiement saisi, la dépense est « Non payée ». Chaque paiement porte sa propre date. Le statut de paiement est indépendant du brouillon et de la soumission.</p>
          </Card>
          </>}
        </aside>
      </form>
      <ReceiptPreviewDialog receipt={preview} onClose={() => setPreview(null)} />
    </div>
  );
}
